import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateLegalDocuments } from "../../Redux/Actions/bookingActions";
import toast from "react-hot-toast";
import "./legalDocument.css";

const UpdateLegalDocumentsPopup = ({ bookingId, onClose, onUpdateSuccess, ID }) => {
  const dispatch = useDispatch();

  // Access booking slice and loading from Redux store under 'bookings'
  const { booking, loading } = useSelector((state) => state.bookings || {});

  // Local component states
  const [legalDocuments, setLegalDocuments] = useState([{ title: "", url: "" }]);
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [loadingLocal, setLoadingLocal] = useState(false);

  // Sync legal documents from Redux booking data when it changes
  useEffect(() => {
    if (booking?.data?.legal_documents?.length) {
      setLegalDocuments(booking.data.legal_documents);
    }
  }, [booking]);

  // Sync loading from redux to local loading state
  useEffect(() => {
    setLoadingLocal(loading);
  }, [loading]);

  // Prevent scrolling while modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  // Close on Escape key if not loading
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && !loadingLocal) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [loadingLocal, onClose]);

  // Input change handler
  const handleInputChange = (index, field, value) => {
    const updatedDocs = [...legalDocuments];
    updatedDocs[index][field] = value;
    setLegalDocuments(updatedDocs);

    setTouchedFields((prev) => ({ ...prev, [`${index}-${field}`]: true }));

    if (errors[`${index}-${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${index}-${field}`];
      setErrors(newErrors);
    }
  };

  // Blur handler triggers validation for touched field
  const handleBlur = (index, field) => {
    setTouchedFields((prev) => ({ ...prev, [`${index}-${field}`]: true }));
    validateField(index, field);
  };

  // Field validation per title/url rules
  const validateField = (index, field) => {
    const doc = legalDocuments[index];
    const newErrors = { ...errors };

    if (field === "title") {
      if (!doc.title.trim()) newErrors[`${index}-title`] = "Title is required";
      else if (doc.title.length < 3) newErrors[`${index}-title`] = "Title must be at least 3 characters";
      else delete newErrors[`${index}-title`];
    }

    if (field === "url") {
      if (!doc.url.trim()) newErrors[`${index}-url`] = "URL is required";
      else if (!isValidUrl(doc.url)) newErrors[`${index}-url`] = "Please enter a valid URL (must start with http:// or https://)";
      else delete newErrors[`${index}-url`];
    }

    setErrors(newErrors);
  };

  // Helper URL validation function
  const isValidUrl = (str) => {
    try {
      const url = new URL(str);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  // Validate all fields on submit
  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;
    legalDocuments.forEach((doc, index) => {
      if (!doc.title.trim()) {
        newErrors[`${index}-title`] = "Title is required";
        isValid = false;
      } else if (doc.title.length < 3) {
        newErrors[`${index}-title`] = "Title must be at least 3 characters";
        isValid = false;
      }

      if (!doc.url.trim()) {
        newErrors[`${index}-url`] = "URL is required";
        isValid = false;
      } else if (!isValidUrl(doc.url)) {
        newErrors[`${index}-url`] = "Please enter a valid URL";
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  // Add new blank document field
  const addNewDocument = () => {
    setLegalDocuments((prev) => [...prev, { title: "", url: "" }]);
    toast.success("New document field added");
  };

  // Remove a document field, require at least one
  const removeDocument = (index) => {
    if (legalDocuments.length === 1) {
      toast.error("At least one document is required");
      return;
    }
    setLegalDocuments((prev) => prev.filter((_, i) => i !== index));
    const newErrors = { ...errors };
    const newTouched = { ...touchedFields };
    delete newErrors[`${index}-title`];
    delete newErrors[`${index}-url`];
    delete newTouched[`${index}-title`];
    delete newTouched[`${index}-url`];
    setErrors(newErrors);
    setTouchedFields(newTouched);
    toast.success("Document removed");
  };

  // Duplicate a document field
  const duplicateDocument = (index) => {
    const doc = legalDocuments[index];
    const copy = { title: `${doc.title} (Copy)`, url: doc.url };
    setLegalDocuments((prev) => {
      const newDocs = [...prev];
      newDocs.splice(index + 1, 0, copy);
      return newDocs;
    });
    toast.success("Document duplicated");
  };

  // Submit updated documents to Redux action
  const handleSubmit = async () => {
    const allTouched = {};
    legalDocuments.forEach((_, idx) => {
      allTouched[`${idx}-title`] = true;
      allTouched[`${idx}-url`] = true;
    });
    setTouchedFields(allTouched);

    // if (!validateAllFields()) {
    //   toast.error("Please fix all errors before submitting");
    //   return;
    // }

    setLoadingLocal(true);
    try {
      const res = await dispatch(updateLegalDocuments({ booking_id: ID, legal_documents: legalDocuments }));
      if (res?.statusCode === 200) {
        toast.success("Legal documents updated successfully!");
        onUpdateSuccess();
        onClose();
      } else {
        toast.error(res?.message || "Failed to update legal documents");
      }
    } catch (error) {
      toast.error(error?.message || "An error occurred while updating");
    } finally {
      setLoadingLocal(false);
    }
  };

  // Close the popup handler
  const handleClose = () => {
    onClose();
  };

  // Progress bar stats
  const getDocumentStats = () => {
    const total = legalDocuments.length;
    const completed = legalDocuments.filter(
      (doc, idx) =>
        doc.title.trim() &&
        doc.url.trim() &&
        !errors[`${idx}-title`] &&
        !errors[`${idx}-url`]
    ).length;
    return { total, completed };
  };

  const { total, completed } = getDocumentStats();
  const progress = total > 0 ? (completed / total) * 100 : 0;

  return (
    <div className="legal-docs-overlay" onClick={handleClose}>
      <div className="legal-docs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="legal-docs-header">
          <div className="legal-docs-header-content">
            <div className="legal-docs-icon-wrapper">
              <svg className="legal-docs-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="legal-docs-title">Update Legal Documents</h3>
              <p className="legal-docs-subtitle">Booking ID: <span className="legal-docs-booking-id">{bookingId}</span></p>
            </div>
          </div>
          <button className="legal-docs-close-btn" onClick={handleClose} disabled={loadingLocal} aria-label="Close modal"><p>X</p></button>
        </div>

        <div className="legal-docs-progress-container">
          <div className="legal-docs-progress-bar">
            <div className="legal-docs-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="legal-docs-progress-text">{completed} of {total} documents completed</div>
        </div>

        <div className="legal-docs-content">
          {legalDocuments.map((doc, index) => (
            <div className="legal-docs-card" key={index}>
              <div className="legal-docs-card-header">
                <div className="legal-docs-card-number">
                  <span className="legal-docs-number-badge">{index + 1}</span>
                  <span className="legal-docs-card-label">Document {index + 1}</span>
                </div>
                <div className="legal-docs-card-actions">
                  <button className="legal-docs-action-btn legal-docs-duplicate-btn" onClick={() => duplicateDocument(index)} disabled={loadingLocal} title="Duplicate document"><p>+</p></button>
                  {legalDocuments.length > 1 && (
                    <button className="legal-docs-action-btn legal-docs-remove-btn" onClick={() => removeDocument(index)} disabled={loadingLocal} title="Remove document"><p>X</p></button>
                  )}
                </div>
              </div>

              <div className="legal-docs-input-group">
                <label className="legal-docs-label">Document Title</label>
                <input
                  type="text"
                  placeholder="e.g., Terms & Conditions, Privacy Policy"
                  value={doc.title}
                  onChange={(e) => handleInputChange(index, "title", e.target.value)}
                  onBlur={() => handleBlur(index, "title")}
                  disabled={loadingLocal}
                  className={`legal-docs-input ${touchedFields[`${index}-title`] && errors[`${index}-title`] ? "legal-docs-input-error" : ""}`}
                />
                {touchedFields[`${index}-title`] && errors[`${index}-title`] && <span className="legal-docs-error-text">{errors[`${index}-title`]}</span>}
              </div>

              <div className="legal-docs-input-group">
                <label className="legal-docs-label">Document URL</label>
                <input
                  type="url"
                  placeholder="https://example.com/document.pdf"
                  value={doc.url}
                  onChange={(e) => handleInputChange(index, "url", e.target.value)}
                  onBlur={() => handleBlur(index, "url")}
                  disabled={loadingLocal}
                  className={`legal-docs-input ${touchedFields[`${index}-url`] && errors[`${index}-url`] ? "legal-docs-input-error" : ""}`}
                />
                {touchedFields[`${index}-url`] && errors[`${index}-url`] && <span className="legal-docs-error-text">{errors[`${index}-url`]}</span>}
              </div>
            </div>
          ))}

          <button className="legal-docs-add-btn" onClick={addNewDocument} disabled={loadingLocal}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add Another Document
          </button>
        </div>

        <div className="legal-docs-footer">
          <div className="legal-docs-footer-info">
            <span
              style={{
                cursor: 'pointer',
                borderRadius: '50%',
                border: '1px solid #000',
                padding: '0 5px',
                fontWeight: '900',
                fontSize: '12px',
                display: 'inline-block',
                lineHeight: '18px',
                textAlign: 'center',
                width: '8px',
                height: '18px'
              }}
              title="Please fill all fields to proceed."
              aria-label="Information"
              role="img"
            >
              i
            </span>
            All fields are required
          </div>
          <div className="legal-docs-footer-actions">
            <button className="legal-docs-cancel-btn" onClick={handleClose} disabled={loadingLocal}>Cancel</button>
            <button className="legal-docs-submit-btn" onClick={handleSubmit} disabled={loadingLocal || Object.keys(errors).length > 0}>
              {loadingLocal ? <><span className="legal-docs-spinner"></span>Updating...</> : <>Update Documents</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateLegalDocumentsPopup;
